import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, withPhotoLinks } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  PhoneOutgoing,
  MessageCircle,
  ClipboardList,
  Play,
  Pause,
  RotateCw,
  CheckCircle2,
  Lock,
  Plus,
  CalendarClock,
  Search,
} from "lucide-react";
import DialButton from "@/components/DialButton";
import CopyButton from "@/components/CopyButton";
import ImageLightbox from "@/components/ImageLightbox";
import TaskDateFilter, { filterTasksByDate } from "@/components/TaskDateFilter";
import { compressImage } from "@/lib/imageCompress";
import { Camera, ImagePlus, X, CheckSquare } from "lucide-react";

const TABS = [
  { key: "pending", label: "Open" },
  { key: "in_progress", label: "Active" },
  { key: "on_hold", label: "On hold" },
  { key: "done", label: "Done" },
];

const STATUS_LABEL = {
  pending: "open",
  in_progress: "active",
  on_hold: "on hold",
  done: "done",
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function EmpTasks() {
  const { user } = useAuth();
  const canCreate = !!user?.can_create_tasks;

  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("pending");
  // Per-status counts for the tab badges — fetched separately (and much
  // more cheaply) from the active tab's task list below, since a count
  // query never has to touch any embedded photo.
  const [counts, setCounts] = useState({});
  // Keyword search: searches every tab on the server (q = what's typed,
  // query = the same after a short pause, so we don't fetch on every key).
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [teammates, setTeammates] = useState([]);
  const [creating, setCreating] = useState(false);
  // Date-range filter (Created / Due / Completed) — applies across all tabs.
  const [dateFilter, setDateFilter] = useState({ field: "created_at", from: "", to: "" });
  // Are we clocked in right now? Mobile No. 2 is hidden until the employee
  // checks in so alternate contact details are only visible during on-duty
  // hours — reduces the risk of customers being called after hours.
  const [checkedIn, setCheckedIn] = useState(false);
  // true while the create dialog is open in "Schedule Task" mode — same
  // dialog, just with an extra required date field and different copy.
  const [scheduling, setScheduling] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    customer_name: "",
    customer_phone: "",
    customer_phone_2: "",
    assigned_to: "",
    priority: "medium",
    scheduled_date: "",
  });

  // Comment dialog state — used for both Hold and Done. When mode is "done"
  // the employee must also attach 1-3 proof-of-work photos before the
  // dialog will submit.
  const [commentDlg, setCommentDlg] = useState({
    open: false,
    task: null,
    mode: "done", // "done" | "hold"
    text: "",
    photos: [], // data URIs, only used when mode === "done"
    uploading: false,
  });
  // Lightbox — used for customer ticket attachments on the task card.
  const [lightbox, setLightbox] = useState(null);

  // Fetches only the active tab's tasks — not the whole work list — so a
  // phone on a slow connection isn't forced to download every Done task's
  // proof-of-work photos just to show 3 open jobs.
  const load = async () => {
    try {
      const params = query ? { q: query } : { status: tab };
      const { data } = await api.get("/tasks", { params });
      setTasks(data.map(withPhotoLinks));
    } catch {
      /* auth interceptor handles */
    }
  };

  const loadCounts = async () => {
    try {
      const { data } = await api.get("/tasks/counts");
      setCounts(data || {});
    } catch {
      /* badges just stay at their last known value */
    }
  };

  // Used after any mutation that can move a task between tabs (status
  // change, new task, etc.) — refreshes the visible list and the badges.
  const refresh = () => Promise.all([load(), loadCounts()]);

  const loadAttendance = async () => {
    try {
      const { data } = await api.get("/attendance/mine");
      // Employee is "checked in" when they have a record for today AND
      // haven't clocked out yet. The record shape comes from
      // /api/attendance/mine — present_today + record.{checked_in_at, checked_out_at}
      const rec = data?.record || {};
      const isIn = !!data?.present_today && !!rec.checked_in_at && !rec.checked_out_at;
      setCheckedIn(isIn);
    } catch {
      setCheckedIn(false);
    }
  };

  const loadTeammates = async () => {
    if (!canCreate) return;
    try {
      const { data } = await api.get("/employees");
      setTeammates(data || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    // While searching, results already cover every tab: switching tabs
    // just shows another part of them.
    if (!query) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Searching: jump to a tab that has matches if the open one has none.
  useEffect(() => {
    if (!query || tasks.some((t) => t.status === tab)) return;
    const hit = TABS.find((tt) => tasks.some((t) => t.status === tt.key));
    if (hit) setTab(hit.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, query]);

  useEffect(() => {
    loadCounts();
    loadTeammates();
    loadAttendance();
    // Re-check attendance every minute so the second-mobile toggle keeps
    // pace with clock-in/out that happens in another tab.
    const t = setInterval(loadAttendance, 60_000);
    return () => clearInterval(t);
  }, [canCreate]);

  const setStatus = async (t, status, extra = {}) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status, ...extra });
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update task");
    }
  };

  // Required-comment prompts. Replaces window.prompt() which is blocked
  // in installed PWAs and some mobile browsers.
  const hold = (t) => {
    setCommentDlg({ open: true, task: t, mode: "hold", text: "", photos: [], uploading: false });
  };

  const markDone = (t) => {
    setCommentDlg({ open: true, task: t, mode: "done", text: "", photos: [], uploading: false });
  };

  const onProofPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 3 - commentDlg.photos.length;
    if (room <= 0) {
      toast.error("Up to 3 proof photos per task.");
      return;
    }
    const picked = files.slice(0, room);
    setCommentDlg((c) => ({ ...c, uploading: true }));
    const added = [];
    for (const file of picked) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
        toast.error(`Skipped ${file.name}: only JPEG/PNG/WebP allowed.`);
        continue;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: over 15 MB.`);
        continue;
      }
      try {
        const r = await compressImage(file);
        added.push(r.dataUrl);
      } catch {
        toast.error(`Could not read ${file.name}.`);
      }
    }
    setCommentDlg((c) => ({
      ...c,
      photos: [...c.photos, ...added].slice(0, 3),
      uploading: false,
    }));
  };

  const removeProofPhoto = (idx) => {
    setCommentDlg((c) => ({ ...c, photos: c.photos.filter((_, i) => i !== idx) }));
  };

  const submitComment = async () => {
    const text = commentDlg.text.trim();
    if (!text) {
      toast.error("A comment is required to continue.");
      return;
    }
    if (commentDlg.mode === "done" && commentDlg.photos.length === 0) {
      toast.error("At least one proof photo is required to mark this task done.");
      return;
    }
    const payload =
      commentDlg.mode === "done"
        ? { status: "done", done_comment: text, proof_photos: commentDlg.photos }
        : { status: "on_hold", hold_reason: text };
    try {
      await api.patch(`/tasks/${commentDlg.task.id}`, payload);
      toast.success(
        commentDlg.mode === "done" ? "Marked done" : "Marked on hold"
      );
      setCommentDlg({ open: false, task: null, mode: "done", text: "", photos: [], uploading: false });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update task");
    }
  };

  const call = async (t) => {
    try {
      const { data } = await api.post("/calls/initiate", {
        task_id: t.id,
        customer_name: t.customer_name,
        notes: `From task: ${t.title}`,
      });
      toast.success(data.message || "Your phone will ring in a few seconds.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start call.");
    }
  };

  const whatsapp = (t) => {
    const text = window.prompt(
      "Message to send to the customer:",
      `Hi ${t.customer_name}, regarding: ${t.title}. `
    );
    if (!text || !text.trim()) return;
    api
      .post("/whatsapp/send", { task_id: t.id, text: text.trim() })
      .then(() => toast.success("Sent from company WhatsApp"))
      .catch((err) =>
        toast.error(
          err?.response?.data?.detail || "Owner: setup Company WhatsApp first"
        )
      );
  };

  const openCreate = (isScheduled) => {
    setScheduling(isScheduled);
    setForm({
      title: "",
      description: "",
      customer_name: "",
      customer_phone: "",
      customer_phone_2: "",
      assigned_to: user?.id || "",  // default to self
      priority: "medium",
      scheduled_date: "",
    });
    setCreating(true);
  };

  const submitCreate = async () => {
    if (!form.title || !form.customer_name || !form.assigned_to) {
      toast.error("Fill title, customer name and assignee");
      return;
    }
    if (!form.customer_phone) {
      toast.error("Customer phone is required");
      return;
    }
    if (scheduling && !form.scheduled_date) {
      toast.error("Pick the date this task should appear");
      return;
    }
    try {
      await api.post("/tasks", { ...form, scheduled_date: scheduling ? form.scheduled_date : null });
      toast.success(scheduling ? "Task scheduled" : "Task created");
      setCreating(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create task");
    }
  };

  const filteredTasks = filterTasksByDate(tasks, dateFilter);

  // Flip one complaint inside a customer ticket between open ↔ done.
  const toggleComplaint = async (taskId, complaintId, nextStatus) => {
    try {
      const { data } = await api.patch(
        `/tasks/${taskId}/complaints/${complaintId}`,
        { status: nextStatus }
      );
      setTasks((cur) =>
        cur.map((t) =>
          t.id === taskId
            ? {
                ...t,
                ticket_complaints: (t.ticket_complaints || []).map((c) =>
                  c.id === complaintId ? data.complaint : c
                ),
              }
            : t
        )
      );
      toast.success(
        nextStatus === "done"
          ? `Marked resolved (${data.done_count}/${data.total})`
          : "Reopened"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update complaint.");
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="emp-tasks">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">
            Work list
          </div>
          <h1 className="font-heading text-2xl font-bold mt-1">Tasks</h1>
          <div className="mono text-[10px] text-slate-500 flex items-center gap-1 mt-1">
            <Lock className="w-3 h-3" />
            {canCreate
              ? "You can create tasks. Only the owner can edit or delete."
              : "Update progress only — task details are managed by your owner"}
          </div>
        </div>
        {canCreate && (
          <div className="flex flex-col gap-1.5 shrink-0">
            <Button
              size="sm"
              onClick={() => openCreate(false)}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="emp-tasks-new"
            >
              <Plus className="w-4 h-4 mr-1" /> New task
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openCreate(true)}
              data-testid="emp-tasks-schedule-new"
            >
              <CalendarClock className="w-4 h-4 mr-1" /> Schedule Task
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          type="search"
          placeholder="Search tasks: name, title, #number…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 pr-9"
          data-testid="emp-tasks-search"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
            aria-label="Clear search"
            data-testid="emp-tasks-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500" data-testid="emp-tasks-search-summary">
          {query &&
            (filteredTasks.length
              ? `${filteredTasks.length} task${filteredTasks.length === 1 ? "" : "s"} match “${query}”`
              : `No tasks match “${query}”`)}
        </div>
        <TaskDateFilter value={dateFilter} onChange={setDateFilter} testId="emp-tasks-date-filter" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full h-auto">
          {TABS.map((t) => {
            // While searching, the badges count matches instead.
            const count = query
              ? filteredTasks.filter((x) => x.status === t.key).length
              : counts[t.key] || 0;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="flex-col gap-0 py-1.5 text-[11px]"
                data-testid={`emp-tasks-tab-${t.key}`}
              >
                <span>{t.label}</span>
                <span className="mono text-[9px] text-slate-500">{count}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {TABS.map((tt) => (
          <TabsContent key={tt.key} value={tt.key} className="space-y-2 mt-4">
            {filteredTasks
              .filter((t) => t.status === tt.key)
              .map((t) => {
                const isOnHold = t.status === "on_hold";
                const isHigh = t.priority === "high";
                return (
                  <Card
                    key={t.id}
                    className={`shadow-none ${
                      isHigh
                        ? "border-red-300 border-l-4 border-l-red-500 bg-red-50/50"
                        : isOnHold
                          ? "border-slate-200 border-l-4 border-l-amber-400"
                          : "border-slate-200"
                    }`}
                    data-testid={`emp-task-${t.id}`}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex justify-between items-start gap-3">
                        <div className="min-w-0">
                          <div className={`font-medium text-sm ${isHigh ? "text-red-900" : ""}`}>
                            {t.task_no != null && (
                              <span
                                className="mono text-[10px] text-slate-400 mr-1.5"
                                data-testid={`emp-task-no-${t.id}`}
                              >
                                #{t.task_no}
                              </span>
                            )}
                            {t.title}
                          </div>
                          <div className="mono text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span>{t.customer_name} · {t.customer_phone}</span>
                            {t.customer_phone && (
                              <DialButton mode="exotel" taskId={t.id} number={t.customer_phone} size="xs" />
                            )}
                          </div>
                          {checkedIn && t.customer_phone_2 && (
                            <div
                              className="mono text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5"
                              data-testid={`emp-task-phone2-${t.id}`}
                            >
                              <span>Alt · {t.customer_phone_2}</span>
                              <DialButton number={t.customer_phone_2} size="xs" />
                              <CopyButton value={t.customer_phone_2} size="xs" />
                            </div>
                          )}
                          {t.description && (
                            <div
                              className="mt-2 border-l-4 border-l-[#0055FF] bg-blue-50/60 rounded px-3 py-2"
                              data-testid={`emp-task-problem-${t.id}`}
                            >
                              <div className="mono text-[10px] uppercase tracking-widest text-[#0055FF] flex items-center gap-1 mb-1">
                                <ClipboardList className="w-3 h-3" /> Customer problem
                              </div>
                              <div className="text-xs text-slate-700 whitespace-pre-wrap">
                                {t.description}
                              </div>
                            </div>
                          )}
                          {isOnHold && t.hold_reason && (
                            <div className="mono text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
                              Hold reason: {t.hold_reason}
                            </div>
                          )}
                          {t.status === "done" && t.done_comment && (
                            <div
                              className="mono text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2 inline-block"
                              data-testid={`emp-task-done-comment-${t.id}`}
                            >
                              Outcome: {t.done_comment}
                            </div>
                          )}
                          {/* Done before, then reassigned: the job came back. */}
                          {t.status !== "done" && t.reopened_at && (
                            <div
                              className="mono text-[10px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1 mt-2 inline-block max-w-full whitespace-pre-wrap break-words"
                              data-testid={`emp-task-reopened-${t.id}`}
                            >
                              Reopened: this was done before
                              {(() => {
                                const h = (t.assignment_history || []).slice(-1)[0];
                                return h?.from_name ? ` by ${h.from_name}` : "";
                              })()}
                              {t.done_comment ? `. Earlier outcome: ${t.done_comment}` : "."}
                            </div>
                          )}
                          {/* Multi-complaint ticket view — each complaint is a
                              tappable row with its own photos and a "done" toggle. */}
                          {Array.isArray(t.ticket_complaints) && t.ticket_complaints.length > 0 ? (
                            <div
                              className="mt-2 space-y-1.5"
                              data-testid={`emp-task-ticket-complaints-${t.id}`}
                            >
                              {t.ticket_complaints.length > 1 && (
                                <div className="mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center justify-between">
                                  <span>Complaints</span>
                                  <span>
                                    {t.ticket_complaints.filter((c) => c.status === "done").length}/{t.ticket_complaints.length} resolved
                                  </span>
                                </div>
                              )}
                              {t.ticket_complaints.map((c, idx) => {
                                const done = c.status === "done";
                                return (
                                  <div
                                    key={c.id || idx}
                                    className={
                                      "rounded border p-1.5 text-xs " +
                                      (done ? "border-emerald-200 bg-emerald-50/40" : "border-fuchsia-200 bg-fuchsia-50/30")
                                    }
                                    data-testid={`emp-task-complaint-${t.id}-${idx}`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <button
                                        type="button"
                                        onClick={() => toggleComplaint(t.id, c.id, done ? "open" : "done")}
                                        className={
                                          "shrink-0 mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center transition " +
                                          (done
                                            ? "bg-emerald-500 border-emerald-500 text-white"
                                            : "border-slate-300 hover:border-emerald-400")
                                        }
                                        title={done ? "Reopen" : "Mark resolved"}
                                        data-testid={`emp-task-complaint-toggle-${t.id}-${idx}`}
                                      >
                                        {done && <CheckSquare className="w-3 h-3" />}
                                      </button>
                                      <span
                                        className={
                                          "flex-1 " +
                                          (done ? "line-through text-slate-400" : "text-slate-700")
                                        }
                                      >
                                        {t.ticket_complaints.length > 1 && (
                                          <b className="mono text-[10px] text-slate-500 mr-1">#{idx + 1}</b>
                                        )}
                                        {c.description}
                                      </span>
                                    </div>
                                    {c.attachments && c.attachments.length > 0 && (
                                      <div className="mt-1.5 grid grid-cols-3 gap-1 pl-6">
                                        {c.attachments.map((src, pIdx) => (
                                          <button
                                            key={pIdx}
                                            type="button"
                                            onClick={() =>
                                              setLightbox({
                                                images: c.attachments,
                                                startIndex: pIdx,
                                                title: `Complaint ${idx + 1} (${pIdx + 1}/${c.attachments.length})`,
                                              })
                                            }
                                            className="aspect-square rounded border border-fuchsia-200 overflow-hidden hover:ring-2 hover:ring-fuchsia-400 transition"
                                            data-testid={`emp-task-complaint-photo-${t.id}-${idx}-${pIdx}`}
                                            title="Tap to view full-size"
                                          >
                                            <img src={src} loading="lazy" decoding="async" alt={`Complaint ${idx + 1} photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (() => {
                            const imgs = (t.ticket_attachments && t.ticket_attachments.length)
                              ? t.ticket_attachments
                              : (t.ticket_attachment ? [t.ticket_attachment] : []);
                            if (!imgs.length) return null;
                            return (
                              <div
                                className="grid grid-cols-3 gap-1 mt-2"
                                data-testid={`emp-task-ticket-photos-${t.id}`}
                              >
                                {imgs.map((src, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => setLightbox({ images: imgs, startIndex: idx, title: `Customer photo (${idx + 1}/${imgs.length})` })}
                                    className="aspect-square rounded border border-fuchsia-200 overflow-hidden hover:ring-2 hover:ring-fuchsia-400 transition"
                                    data-testid={`emp-task-ticket-photo-${t.id}-${idx}`}
                                    title="Tap to view full-size"
                                  >
                                    <img src={src} loading="lazy" decoding="async" alt={`Customer photo ${idx + 1}`} className="w-full h-full object-cover" />
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <span
                          className={`mono text-[10px] uppercase px-2 py-1 rounded font-bold ${
                            t.priority === "high"
                              ? "bg-red-600 text-white shadow-sm"
                              : t.priority === "low"
                                ? "bg-slate-100 text-slate-600"
                                : "bg-blue-50 text-[#0055FF]"
                          }`}
                        >
                          {t.priority}
                        </span>
                      </div>
                      <div className="flex gap-2 pt-1 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => call(t)}
                          className="bg-[#0055FF] hover:bg-[#0040CC]"
                          data-testid={`emp-task-call-${t.id}`}
                        >
                          <PhoneOutgoing className="w-3.5 h-3.5 mr-1" /> Call
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => whatsapp(t)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                          data-testid={`emp-task-whatsapp-${t.id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                        </Button>

                        {t.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatus(t, "in_progress")}
                            data-testid={`emp-task-start-${t.id}`}
                          >
                            <Play className="w-3.5 h-3.5 mr-1" /> Start
                          </Button>
                        )}

                        {t.status === "on_hold" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatus(t, "in_progress")}
                            data-testid={`emp-task-resume-${t.id}`}
                          >
                            <RotateCw className="w-3.5 h-3.5 mr-1" /> Resume
                          </Button>
                        )}

                        {(t.status === "pending" ||
                          t.status === "in_progress") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => hold(t)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                            data-testid={`emp-task-hold-${t.id}`}
                          >
                            <Pause className="w-3.5 h-3.5 mr-1" /> Hold
                          </Button>
                        )}

                        {t.status !== "done" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markDone(t)}
                            data-testid={`emp-task-done-${t.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            {filteredTasks.filter((t) => t.status === tt.key).length === 0 && (
              <div className="text-sm text-slate-500 text-center p-6">
                Nothing here.
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Create task dialog (only for employees with can_create_tasks) */}
      {canCreate && (
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogContent data-testid="emp-tasks-create-dialog">
            <DialogHeader>
              <DialogTitle>{scheduling ? "Schedule task" : "New task"}</DialogTitle>
              {scheduling && (
                <p className="text-xs text-slate-500">
                  Stays hidden until the date below, then appears in Open automatically.
                </p>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Task title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Follow up quotation"
                  data-testid="emp-tasks-create-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Customer name</Label>
                  <Input
                    value={form.customer_name}
                    onChange={(e) =>
                      setForm({ ...form, customer_name: e.target.value })
                    }
                    data-testid="emp-tasks-create-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={form.customer_phone}
                    onChange={(e) =>
                      setForm({ ...form, customer_phone: e.target.value })
                    }
                    placeholder="+91…"
                    data-testid="emp-tasks-create-customer-phone"
                  />
                </div>
              </div>
              {checkedIn && (
                <div className="space-y-2" data-testid="emp-tasks-create-phone2-wrap">
                  <Label>
                    Mobile No. 2 <span className="text-slate-400 text-xs">(optional)</span>
                  </Label>
                  <Input
                    value={form.customer_phone_2}
                    onChange={(e) => setForm({ ...form, customer_phone_2: e.target.value })}
                    placeholder="Alternate contact"
                    data-testid="emp-tasks-create-customer-phone-2"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Assign to</Label>
                  <Select
                    value={form.assigned_to}
                    onValueChange={(v) =>
                      setForm({ ...form, assigned_to: v })
                    }
                  >
                    <SelectTrigger data-testid="emp-tasks-create-assignee">
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent>
                      {teammates.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.id === user?.id ? "Me" : e.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(v) => setForm({ ...form, priority: v })}
                  >
                    <SelectTrigger data-testid="emp-tasks-create-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Customer problem / notes</Label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  data-testid="emp-tasks-create-description"
                />
              </div>
              {scheduling && (
                <div className="space-y-2">
                  <Label>Appear on date</Label>
                  <Input
                    type="date"
                    min={todayStr()}
                    value={form.scheduled_date}
                    onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                    data-testid="emp-tasks-create-scheduled-date"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreating(false)}
                data-testid="emp-tasks-create-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitCreate}
                className="bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="emp-tasks-create-submit"
              >
                {scheduling ? "Schedule task" : "Create task"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Comment dialog for Hold / Done — required in-page form so it works
          in installed PWAs and mobile browsers that block window.prompt(). */}
      <Dialog
        open={commentDlg.open}
        onOpenChange={(v) =>
          setCommentDlg((c) => ({ ...c, open: v, text: v ? c.text : "" }))
        }
      >
        <DialogContent data-testid="emp-tasks-comment-dialog">
          <DialogHeader>
            <DialogTitle>
              {commentDlg.mode === "done"
                ? "Add outcome to close this task"
                : "Why is this task on hold?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>
              {commentDlg.mode === "done"
                ? "Outcome / comment (required)"
                : "Reason (required)"}
            </Label>
            <Textarea
              rows={4}
              autoFocus
              value={commentDlg.text}
              onChange={(e) =>
                setCommentDlg((c) => ({ ...c, text: e.target.value }))
              }
              placeholder={
                commentDlg.mode === "done"
                  ? "e.g. Called customer; deal closed at ₹45,000"
                  : "e.g. Customer asked to call back next Monday"
              }
              data-testid="emp-tasks-comment-input"
            />
            <div className="mono text-[10px] text-slate-500">
              Your owner will see this on their dashboard.
            </div>
          </div>
          {commentDlg.mode === "done" && (
            <div className="space-y-2 mt-3 border-t border-slate-100 pt-3">
              <Label className="flex items-center gap-1.5">
                <ImagePlus className="w-3.5 h-3.5 text-emerald-600" />
                Proof photos <span className="text-red-600 text-xs">(required — up to 3)</span>
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="emp-proof-photos-picker">
                {commentDlg.photos.map((src, idx) => (
                  <div key={idx} className="relative aspect-square">
                    <img
                      src={src}
                      alt={`Proof ${idx + 1}`}
                      className="w-full h-full object-cover rounded-md border border-emerald-200 bg-slate-50"
                      data-testid={`emp-proof-photo-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeProofPhoto(idx)}
                      className="absolute top-1 right-1 bg-white/95 border border-slate-200 rounded-full p-1 hover:bg-red-50 hover:text-red-600 shadow-sm"
                      data-testid={`emp-proof-photo-remove-${idx}`}
                      title="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {commentDlg.photos.length < 3 && (
                  <>
                    <label
                      className="aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-emerald-200 hover:border-emerald-500 rounded-md cursor-pointer transition text-xs text-emerald-700"
                      data-testid="emp-proof-camera-label"
                    >
                      <Camera className="w-5 h-5" />
                      <span>Camera</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={onProofPhotos}
                        data-testid="emp-proof-camera-input"
                      />
                    </label>
                    {commentDlg.photos.length < 2 && (
                      <label
                        className="aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-md cursor-pointer transition text-xs text-slate-500"
                        data-testid="emp-proof-file-label"
                      >
                        <ImagePlus className="w-5 h-5" />
                        <span>Gallery</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          className="hidden"
                          onChange={onProofPhotos}
                          data-testid="emp-proof-file-input"
                        />
                      </label>
                    )}
                  </>
                )}
              </div>
              {commentDlg.uploading && (
                <div className="mono text-[10px] text-slate-500">Compressing photos...</div>
              )}
              <div className="mono text-[10px] text-slate-500">
                Snap the work-completed photo(s). At least one is required to submit.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setCommentDlg({ open: false, task: null, mode: "done", text: "", photos: [], uploading: false })
              }
              data-testid="emp-tasks-comment-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitComment}
              disabled={
                !commentDlg.text.trim() ||
                commentDlg.uploading ||
                (commentDlg.mode === "done" && commentDlg.photos.length === 0)
              }
              className={
                commentDlg.mode === "done"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-amber-600 hover:bg-amber-700"
              }
              data-testid="emp-tasks-comment-submit"
            >
              {commentDlg.mode === "done" ? "Mark done" : "Put on hold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          title={lightbox.title}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
